import React, { Component } from 'react';
import { DropDown, SelectPanel } from '..';
import { PostalCodeAndRadiusSelectPanel } from './PostalCodeAndRadiusSelectPanel';
import {
    Dictionary,
    IUsefullContactFilterParams,
    IPostalCode,
} from '../../Models';

interface IDirectoryFiltersState {
    selectedLanguage: Dictionary<number, string> | null;
    selectedType: string[] | null;
    selectedPostalCode: IPostalCode | null;
    selectedRadiusKilometer: Dictionary<number, string> | null;
    selectedFieldOfSupport: string[] | null;
}

interface IDirectoryFiltersProps {
    usefullContactFilterParams: IUsefullContactFilterParams;
    filterUsefullContactList: (
        usefullContactFilterParams: IUsefullContactFilterParams
    ) => void;
    fieldOfSupportList: string[];
    postalCodesList: (postalCodeParams?: string) => Promise<IPostalCode[]>;
    typesList: string[];
    usefullContactPartnerName: string;
    radiusKilometers: Dictionary<number, string>[];
    postalCodeAndRadius: string;
    cancel: string;
    save: string;
    type: string;
    field: string;
    fieldOfSupport: string;
    language: string;
    postalCode: string;
    radius: string;
    dictRemoveFilter: string;
    dictUsefullContactName: string;
    dictSearch: string;
    dictSearchUsefullContact: string;
}

export class DirectoryFilters extends Component<
    IDirectoryFiltersProps,
    IDirectoryFiltersState
> {
    private languageDictionary: Dictionary<number, string>[] = [
        { value: 1, label: 'FR' },
        { value: 2, label: 'NL' },
    ];
    private typeDropdownRef = React.createRef<DropDown>();
    private fieldsOfSupportDropdownRef = React.createRef<DropDown>();
    private postalCodeAndRadiusDropdownRef = React.createRef<DropDown>();

    constructor(props: any) {
        super(props);
        let anchorValue;
        const url = document.location;
        const strippedUrl = url.toString().split('#');
        if (strippedUrl.length > 1) anchorValue = strippedUrl[1];

        this.state = {
            selectedLanguage: this.setPrefilter(),
            selectedType: anchorValue
                ? [anchorValue]
                : this.props.usefullContactFilterParams.type,
            selectedPostalCode: null,
            selectedRadiusKilometer: null,
            selectedFieldOfSupport:
                this.props.usefullContactFilterParams.fieldsOfSupport,
        };
    }

    componentDidUpdate(prevProps: IDirectoryFiltersProps) {
        const prevTypes = prevProps.usefullContactFilterParams.type ?? [];
        const nextTypes = this.props.usefullContactFilterParams.type ?? [];

        const changed =
            prevTypes.length !== nextTypes.length ||
            prevTypes.some((t, i) => t !== nextTypes[i]);

        if (changed) {
            this.setState({
                selectedType: nextTypes,
            });
        }
    }

    getPostalCodeAndRadiusLabel(): React.ReactText {
        if (
            this.state.selectedPostalCode &&
            this.state.selectedRadiusKilometer
        ) {
            return (
                this.state.selectedPostalCode.postalCode +
                ' - ' +
                this.state.selectedRadiusKilometer.label +
                ' km'
            );
        } else if (
            this.state.selectedPostalCode &&
            !this.state.selectedRadiusKilometer
        ) {
            return this.state.selectedPostalCode.city.toString();
        } else if (
            !this.state.selectedPostalCode &&
            this.state.selectedRadiusKilometer
        ) {
            return this.state.selectedRadiusKilometer.label + ' km';
        }

        return this.props.postalCodeAndRadius;
    }

    onSelectLanguage = (optionsList: string[]) => {
        (this.refs.languageDropdown as DropDown).handleClose();
        let selectedLanguage: Dictionary<number, string> | null = null;
        if (optionsList && optionsList.length > 0) {
            const value = this.languageDictionary.find(
                (l) => l.label == optionsList[0]
            );
            if (value) {
                selectedLanguage = {
                    value: value.value,
                    label: optionsList[0],
                };
            }
        }

        this.setState({ selectedLanguage }, () =>
            this.updateUsefullContactList()
        );
    };

    onSelectType = (optionsList: string[]) => {
        const storeSelectedType: string[] = [];
        if (optionsList) {
            for (let i = 0; i < optionsList.length; i++) {
                storeSelectedType.push(optionsList[i]);
            }
        }

        let selectedFieldOfSupport: string[] | null = null;
        let selectedLanguage: Dictionary<number, string> | null = null;
        const partnerName = this.props.usefullContactPartnerName.toUpperCase();
        const oldHasPartner =
            this.state.selectedType &&
            this.state.selectedType
                .map((t) => t.toUpperCase())
                .indexOf(partnerName) !== -1;
        const newHasPartner =
            storeSelectedType
                .map((t) => t.toUpperCase())
                .indexOf(partnerName) !== -1;
        if (!newHasPartner && oldHasPartner) {
            // reset domain and language dropdowns
            selectedFieldOfSupport = null;
            selectedLanguage = null;
        }

        this.typeDropdownRef.current?.handleClose();
        this.setState(
            {
                selectedType: storeSelectedType,
                selectedFieldOfSupport,
                selectedLanguage,
            },
            () => this.updateUsefullContactList()
        );
    };

    onSelectFieldOfSupport = (optionsList: string[]) => {
        let storeSelectedFieldOfSupport = this.state.selectedFieldOfSupport;
        storeSelectedFieldOfSupport = [];
        if (optionsList) {
            for (let i = 0; i < optionsList.length; i++) {
                storeSelectedFieldOfSupport.push(optionsList[i]);
            }
        }

        this.fieldsOfSupportDropdownRef.current?.handleClose();
        this.setState(
            {
                selectedFieldOfSupport: storeSelectedFieldOfSupport,
            },
            () => this.updateUsefullContactList()
        );
    };

    onSelectPostalCodeAndRadius = (
        postalCode: IPostalCode | null,
        radius: Dictionary<number, string> | null
    ) => {
        this.postalCodeAndRadiusDropdownRef.current?.handleClose();
        this.setState(
            {
                selectedPostalCode: postalCode,
                selectedRadiusKilometer: radius,
            },
            () => this.updateUsefullContactList()
        );
    };

    setPrefilter(): Dictionary<number, string> | null {
        if (
            this.props.usefullContactFilterParams &&
            this.props.usefullContactFilterParams.language
        ) {
            const language = this.languageDictionary.find(
                (l) => l.label == this.props.usefullContactFilterParams.language
            );
            if (language) {
                return language;
            }
        }
        return null;
    }

    updateUsefullContactList() {
        this.props.filterUsefullContactList({
            type: this.state.selectedType ? this.state.selectedType : [],
            language: this.state.selectedLanguage
                ? this.state.selectedLanguage.label
                : null,
            fieldsOfSupport: this.state.selectedFieldOfSupport
                ? this.state.selectedFieldOfSupport
                : [],
            postalCode: this.state.selectedPostalCode,
            radius: this.state.selectedRadiusKilometer
                ? this.state.selectedRadiusKilometer.value
                : null,
            title: null,
        });
    }

    render() {
        const usefullContactPartnerName =
            this.props.usefullContactPartnerName.toUpperCase();
        const showDomainAndLanguageDropDown =
            this.state.selectedType &&
            this.state.selectedType.length > 0 &&
            this.state.selectedType.some(
                (t) => t.toUpperCase() == usefullContactPartnerName
            );

        return (
            <>
                <DropDown
                    key={this.props.typesList.join('-')}
                    ref={this.typeDropdownRef}
                    dropdownId={'typeDropdown'}
                    active={
                        this.state.selectedType
                            ? this.state.selectedType.length > 0
                            : false
                    }
                    label={
                        this.state.selectedType &&
                        this.state.selectedType.length > 0
                            ? this.state.selectedType.length +
                              ' ' +
                              this.props.type
                            : this.props.type
                    }
                >
                    {this.props.typesList.length > 0 && (
                        <SelectPanel
                            cancelLabel={this.props.cancel}
                            isMulti
                            label={this.props.type}
                            onClickSave={this.onSelectType}
                            options={this.props.typesList}
                            optionsSelected={this.state.selectedType}
                            optionLabel={(option: string) => option}
                            optionValue={(option: string) => option}
                            saveLabel={this.props.save}
                            isFieldsOfSupport={false}
                        />
                    )}
                </DropDown>
                {showDomainAndLanguageDropDown && (
                    <>
                        <DropDown
                            ref={this.fieldsOfSupportDropdownRef}
                            dropdownId={'fieldsOfSupportDropdown'}
                            active={
                                this.state.selectedFieldOfSupport
                                    ? this.state.selectedFieldOfSupport.length >
                                      0
                                    : false
                            }
                            label={
                                this.state.selectedFieldOfSupport &&
                                this.state.selectedFieldOfSupport.length > 0
                                    ? this.state.selectedFieldOfSupport.length +
                                      ' ' +
                                      this.props.field
                                    : this.props.fieldOfSupport
                            }
                        >
                            <SelectPanel
                                cancelLabel={this.props.cancel}
                                isMulti
                                label={this.props.fieldOfSupport}
                                onClickSave={this.onSelectFieldOfSupport}
                                options={this.props.fieldOfSupportList}
                                optionsSelected={
                                    this.state.selectedFieldOfSupport
                                }
                                optionLabel={(option: string) => option}
                                optionValue={(option: string) => option}
                                saveLabel={this.props.save}
                                isFieldsOfSupport={true}
                            />
                        </DropDown>
                    </>
                )}
                <DropDown
                    ref={this.postalCodeAndRadiusDropdownRef}
                    dropdownId={'postalCodeAndRadiusDropdown'}
                    active={
                        this.state.selectedPostalCode != null ||
                        this.state.selectedRadiusKilometer != null
                    }
                    label={this.getPostalCodeAndRadiusLabel()}
                >
                    <PostalCodeAndRadiusSelectPanel
                        cancelLabel={this.props.cancel}
                        onClickSave={this.onSelectPostalCodeAndRadius}
                        postalCodeLabel={this.props.postalCode}
                        promiseOptions={this.props.postalCodesList}
                        radiusLabel={this.props.radius}
                        radiusOptions={this.props.radiusKilometers}
                        saveLabel={this.props.save}
                    />
                </DropDown>
            </>
        );
    }
}
